// src/components/admin/DealershipsPanel.js
// Admin: list every dealership and add or edit one (name, address, contact,
// opening hours, verified badge). New dealerships start on a free trial.
// The server places each dealership on the map from its address.
import React, { useState } from 'react';
import { adminAPI } from '../../services/api';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_HOURS = DAYS.map((_, day) => (day === 0
  ? { day, isOpen: false, open: '09:00', close: '18:00' }
  : { day, isOpen: true, open: '09:00', close: '18:00' }));

const input =
  'mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border outline-none text-[11px]';
const timeInput =
  'w-28 px-2 py-1 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border outline-none text-[11px]';
const label = 'text-app-text-secondary-light dark:text-app-text-secondary text-[11px]';
const chip = 'text-[10px] px-2 py-1 rounded-full bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text';

const STATUS_LABEL = {
  trialing: 'Free trial',
  active: 'Active',
  past_due: 'Payment overdue',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
  unpaid: 'Unpaid',
};

function emptyForm() {
  return {
    name: '', street: '', city: '', state: '', zip: '',
    phone: '', email: '', website: '', verified: false, hours: DEFAULT_HOURS,
  };
}

function formFrom(d) {
  const byDay = Object.fromEntries((d.hours || []).map((h) => [h.day, h]));
  return {
    name: d.name || '',
    street: d.address?.street || '',
    city: d.address?.city || '',
    state: d.address?.state || '',
    zip: d.address?.zip || '',
    phone: d.phone || '',
    email: d.email || '',
    website: d.website || '',
    verified: Boolean(d.verified),
    hours: DEFAULT_HOURS.map((h) => {
      const saved = byDay[h.day];
      if (!saved) return { ...h, isOpen: (d.hours || []).length ? false : h.isOpen };
      return { day: h.day, isOpen: Boolean(saved.isOpen), open: saved.open || h.open, close: saved.close || h.close };
    }),
  };
}

function payloadFrom(form) {
  return {
    name: form.name,
    address: { street: form.street, city: form.city, state: form.state, zip: form.zip },
    phone: form.phone,
    email: form.email,
    website: form.website,
    verified: form.verified,
    hours: form.hours.map((h) => (h.isOpen ? h : { day: h.day, isOpen: false })),
  };
}

const DealershipsPanel = ({ dealers, loading, onSaved }) => {
  const [editing, setEditing] = useState(null); // null | 'new' | dealership id
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };
  const setHours = (day, field, value) => setForm((f) => ({
    ...f,
    hours: f.hours.map((h) => (h.day === day ? { ...h, [field]: value } : h)),
  }));

  // Extend a trial (not for Stripe-billed or open-ended trials; the API checks too)
  const [extendDays, setExtendDays] = useState({});
  const [extending, setExtending] = useState(null);
  const canExtend = (d) => !d.billedByStripe && !(d.subscription?.status === 'trialing' && !d.subscription?.trialEndsAt);
  const extend = async (d) => {
    setExtending(d._id);
    setError('');
    setNotice('');
    try {
      const { data } = await adminAPI.extendTrial(d._id, Number(extendDays[d._id] || 14));
      setNotice(`${data.name}: free trial now runs until ${new Date(data.subscription.trialEndsAt).toLocaleDateString()}.`);
      onSaved(data);
    } catch (err) {
      setNotice('');
      setError(err.response?.data?.message || 'Could not extend the trial.');
    } finally {
      setExtending(null);
    }
  };

  const startNew = () => { setEditing('new'); setForm(emptyForm()); setError(''); setNotice(''); };
  const startEdit = (d) => { setEditing(d._id); setForm(formFrom(d)); setError(''); setNotice(''); };
  const cancel = () => { setEditing(null); setError(''); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = editing === 'new'
        ? await adminAPI.createDealer(payloadFrom(form))
        : await adminAPI.updateDealer(editing, payloadFrom(form));
      setNotice(data.locationKnown
        ? `${data.name} saved.`
        : `${data.name} saved, but we couldn't place this address on the map yet. Check the street and ZIP; the server keeps retrying.`);
      setEditing(null);
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save the dealership.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-3">
      <div className="flex justify-between items-center">
        <div className="font-semibold text-sm">Dealerships</div>
        {!editing && (
          <button type="button" onClick={startNew} className="px-3 py-1.5 rounded-xl bg-app-accent text-black text-[11px] font-semibold">
            Add dealership
          </button>
        )}
      </div>

      {notice && !editing && (
        <div className="bg-green-900/30 text-green-300 px-3 py-2 rounded-xl text-[11px]">{notice}</div>
      )}
      {error && !editing && (
        <div className="bg-red-900/40 text-red-300 px-3 py-2 rounded-xl text-[11px]">{error}</div>
      )}

      {editing && (
        <form onSubmit={save} className="space-y-2 border border-app-border-light dark:border-app-border rounded-xl p-3">
          <div className="font-semibold text-xs">{editing === 'new' ? 'New dealership' : 'Edit dealership'}</div>
          {editing === 'new' && (
            <p className={label}>New dealerships start on a 14-day free trial.</p>
          )}

          <div>
            <label className={label}>Name</label>
            <input className={input} value={form.name} onChange={set('name')} required maxLength={120} />
          </div>
          <div>
            <label className={label}>Street address</label>
            <input className={input} value={form.street} onChange={set('street')} maxLength={120} placeholder="100 W Wisconsin Ave" />
          </div>
          <div className="grid grid-cols-6 gap-2">
            <div className="col-span-3">
              <label className={label}>City</label>
              <input className={input} value={form.city} onChange={set('city')} maxLength={80} />
            </div>
            <div className="col-span-1">
              <label className={label}>State</label>
              <input className={input} value={form.state} onChange={set('state')} maxLength={2} placeholder="WI" />
            </div>
            <div className="col-span-2">
              <label className={label}>ZIP</label>
              <input className={input} value={form.zip} onChange={set('zip')} maxLength={10} inputMode="numeric" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={label}>Phone</label>
              <input className={input} value={form.phone} onChange={set('phone')} maxLength={30} type="tel" />
            </div>
            <div>
              <label className={label}>Email</label>
              <input className={input} value={form.email} onChange={set('email')} maxLength={200} type="email" />
            </div>
          </div>
          <div>
            <label className={label}>Website</label>
            <input className={input} value={form.website} onChange={set('website')} maxLength={200} placeholder="primemotors.com" />
          </div>

          <div>
            <div className={label}>Opening hours</div>
            <div className="mt-1 space-y-1">
              {form.hours.map((h) => (
                <div key={h.day} className="flex items-center gap-2">
                  <label className="flex items-center gap-1 w-28 text-[11px]">
                    <input type="checkbox" checked={h.isOpen} onChange={(e) => setHours(h.day, 'isOpen', e.target.checked)} />
                    {DAYS[h.day]}
                  </label>
                  {h.isOpen ? (
                    <>
                      <input type="time" className={timeInput} value={h.open} onChange={(e) => setHours(h.day, 'open', e.target.value)} required />
                      <span className="text-[11px]">to</span>
                      <input type="time" className={timeInput} value={h.close} onChange={(e) => setHours(h.day, 'close', e.target.value)} required />
                    </>
                  ) : (
                    <span className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">Closed</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-[11px]">
            <input type="checkbox" checked={form.verified} onChange={set('verified')} />
            Verified dealer badge (shown to customers)
          </label>

          {error && <div className="bg-red-900/40 text-red-300 px-3 py-2 rounded-xl text-[11px]">{error}</div>}

          <div className="flex gap-2">
            <button type="button" onClick={cancel} className="flex-1 py-2 rounded-xl bg-app-bg-light dark:bg-app-bg text-[11px] font-semibold">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="flex-1 py-2 rounded-xl bg-app-accent text-black text-[11px] font-semibold disabled:opacity-50">
              {saving ? 'Saving...' : 'Save dealership'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className={label}>Loading dealerships...</div>
      ) : dealers.length === 0 ? (
        <div className={label}>No dealerships yet. Add the first one above.</div>
      ) : (
        <div className="space-y-2">
          {dealers.map((d) => (
            <div key={d._id} className="border border-app-border-light dark:border-app-border rounded-xl p-2 flex flex-col gap-1">
              <div className="flex justify-between items-center gap-2">
                <div className="font-semibold text-xs">{d.name}</div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">ID: {d.dealershipId}</span>
                  {!editing && (
                    <button type="button" onClick={() => startEdit(d)} className="text-[11px] text-app-accent font-semibold">
                      Edit
                    </button>
                  )}
                </div>
              </div>
              <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">
                {[d.address?.street, [d.address?.city, d.address?.state].filter(Boolean).join(', '), d.address?.zip]
                  .filter(Boolean).join(' · ') || 'No address yet'}
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className={chip}>
                  {d.trialExpired
                    ? `Trial ended ${new Date(d.subscription.trialEndsAt).toLocaleDateString()}`
                    : STATUS_LABEL[d.subscription?.status] || 'No subscription'}
                  {d.subscription?.status === 'trialing' && d.subscription?.trialEndsAt && !d.trialExpired
                    ? ` until ${new Date(d.subscription.trialEndsAt).toLocaleDateString()}`
                    : ''}
                </span>
                {d.subscription?.plan && <span className={chip}>{d.subscription.plan} plan</span>}
                <span className={chip}>{d.locationKnown ? 'On the map' : 'Not on the map yet'}</span>
                {d.verified && <span className={chip}>Verified</span>}
              </div>
              {!editing && canExtend(d) && (
                <div className="flex items-center gap-2 mt-1">
                  <select
                    className="px-2 py-1 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-[11px]"
                    value={extendDays[d._id] || 14}
                    onChange={(e) => setExtendDays((x) => ({ ...x, [d._id]: e.target.value }))}
                  >
                    <option value={7}>+7 days</option>
                    <option value={14}>+14 days</option>
                    <option value={30}>+30 days</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => extend(d)}
                    disabled={extending === d._id}
                    className="text-[11px] text-app-accent font-semibold disabled:opacity-50"
                  >
                    {extending === d._id ? 'Extending...' : d.subscription?.status === 'trialing' ? 'Extend trial' : 'Start trial'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealershipsPanel;
